import React, { useEffect, useState } from "react";
import InputFields from "../components/InputFields";
import { useAddCategory, useUpdateCategory } from './../hooks/hooks';
import { showErrorToast, showSuccessToast } from './../components/common'
import { useTranslation } from "react-i18next";

const AddCategory = ({ isOpen, onClose, isEdit, isEditItem }) => {
    const { t } = useTranslation();

    const [inputValue, setInputValue] = useState({ name: "", isActive: true, nameEn: "" });
    const [Error, setError] = useState({ name: "", isActive: true, nameEn: "" });
    const { mutate: addCategory } = useAddCategory();
    const { mutate: updateCategory } = useUpdateCategory();

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isEdit && isEditItem) {
            setInputValue({
                name: isEditItem.categoryName || "",
                nameEn: isEditItem.categoryNameEn || "",
                isActive: isEditItem.isActive || true
            });
        }
    }, [isEdit, isEditItem]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setInputValue({
            ...inputValue,
            [name]: value.toString(),
        });
        setError({ ...Error, name: "", isActive: true, all: "", nameEn: "" });
    };

    const onSubmit = () => {
        if (inputValue.name.toString().trim() === "") {
            setError({ ...Error, name: t('Menu_Name_Er') });
            return;
        }
        setIsLoading(true);

        if (isEdit) {
            const updateItem = {
                name: inputValue.name,
                nameEn: inputValue.nameEn,
                isActive: inputValue.isActive,
                _id: isEditItem._id
            }
            updateCategory(updateItem, {
                onSuccess: (res) => {
                    showSuccessToast(res?.data?.message);
                    onClose(true);
                    setIsLoading(false);
                    setError({ ...Error, name: '' });
                },
                onError: (err) => {
                    setIsLoading(false);
                    setError({ ...Error, all: err.response.data.error });
                },
            });
        } else {
            addCategory(inputValue, {
                onSuccess: (res) => {
                    showSuccessToast(res?.data?.message);
                    onClose(true);
                    setIsLoading(false);
                    setError({ ...Error, name: '' });
                },
                onError: (err) => {
                    setIsLoading(false);
                    showErrorToast(err.response.data.error);
                    setError({ ...Error, all: err.response.data.error });
                },
            });
        }
    }

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black backdrop-blur-sm bg-opacity-50 transition-opacity" onClick={onClose}></div>
            
            {/* Modal */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                {isEdit ? t('update_category_title') : t('Add_category_title')}
                            </h3>
                        </div>
                        <button
                            onClick={() => {
                                onClose(false);
                                setError({ name: "", isActive: true });
                            }}
                            className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                        >
                            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                        <div className="space-y-6">
                            {/* Gujarati Name Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('menuCategory_name_Gu')}
                                </label>
                                <InputFields
                                    name="name"
                                    label="menu name"
                                    type="text"
                                    isHideLabel="false"
                                    isFixedLabel="true"
                                    disabled={isLoading}
                                    handleChange={(e) => handleChange(e)}
                                    value={inputValue?.name}
                                    className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 text-gray-900"
                                />
                                {Error?.name && (
                                    <div className="mt-2 text-sm text-red-600 flex items-center gap-1">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        {Error?.name}
                                    </div>
                                )}
                            </div>

                            {/* English Name Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('menuCategory_name_En')}
                                </label>
                                <InputFields
                                    name="nameEn"
                                    label="menu name"
                                    type="text"
                                    isHideLabel="false"
                                    isFixedLabel="true"
                                    disabled={isLoading}
                                    handleChange={(e) => handleChange(e)}
                                    value={inputValue?.nameEn}
                                    className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 text-gray-900"
                                />
                                {Error?.nameEn && (
                                    <div className="mt-2 text-sm text-red-600 flex items-center gap-1">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        {Error?.nameEn}
                                    </div>
                                )}
                            </div>

                            {/* General Error */}
                            {Error?.all && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <div className="flex items-center gap-2 text-sm text-red-600">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        {Error?.all}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Action Button */}
                        <div className="mt-8">
                            <button
                                onClick={onSubmit}
                                disabled={isLoading}
                                className="w-full px-4 py-3 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isLoading ? (
                                    <>
                                        <svg
                                            aria-hidden="true"
                                            role="status"
                                            className="inline w-4 h-4 text-white animate-spin"
                                            viewBox="0 0 100 101"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                                fill="#E5E7EB"
                                            />
                                            <path
                                                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                                fill="currentColor"
                                            />
                                        </svg>
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                        </svg>
                                        {isEdit ? t('btnUpdate') : t('btnSave')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddCategory;
